import {
  fetchEcosystemHistory,
  fetchEcosystemOverview,
  fetchHighlights,
  fetchMarketHistory,
  fetchMarketLeaderboard,
  fetchMarketStats,
  fetchProviderHistory,
  fetchProviderLeaderboard,
  fetchProviderSummary,
  fetchTrendingMarkets,
  normalizeMarketIdForRoute,
  type AnalyticsHistoryWindow,
  type AnalyticsWindow,
  type EcosystemMetric,
  type MarketInterval,
  type MarketLeaderboardMetric,
  type MarketWindow,
  type ProviderHistoryMetric,
  type ProviderLeaderboardMetric,
  type ProviderSummarySort,
  type TrendingWindow,
} from "@/api/hip3";
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { hip3QueryKeys } from "./hip3-query-keys";

type UseTrendingMarketsOptions = {
  window?: TrendingWindow;
  limit?: number;
  enabled?: boolean;
};

type UseMarketStatsOptions = {
  provider?: string;
  marketId?: string;
  window?: MarketWindow;
  enabled?: boolean;
};

type UseMarketHistoryOptions = {
  provider?: string;
  marketId?: string;
  window?: MarketWindow;
  interval?: MarketInterval;
  enabled?: boolean;
};

type UseEcosystemOverviewOptions = {
  window?: AnalyticsWindow;
  enabled?: boolean;
};

type UseEcosystemHistoryOptions = {
  metric: EcosystemMetric;
  window?: AnalyticsHistoryWindow;
  interval?: MarketInterval;
  enabled?: boolean;
};

type UseProviderSummaryOptions = {
  window?: AnalyticsWindow;
  sort?: ProviderSummarySort;
  enabled?: boolean;
};

type UseProviderHistoryOptions = {
  metric: ProviderHistoryMetric;
  window?: AnalyticsHistoryWindow;
  interval?: MarketInterval;
  enabled?: boolean;
};

type UseMarketLeaderboardOptions = {
  metric: MarketLeaderboardMetric;
  window?: AnalyticsWindow;
  limit?: number;
  provider?: string;
  category?: string;
  enabled?: boolean;
};

type UseProviderLeaderboardOptions = {
  metric: ProviderLeaderboardMetric;
  window?: AnalyticsWindow;
  limit?: number;
  enabled?: boolean;
};

type UseHighlightsOptions = {
  window?: "24h" | "7d";
  enabled?: boolean;
};

export function buildMarketStatsQueryOptions(options: UseMarketStatsOptions) {
  const provider = options.provider?.trim() ?? "";
  const marketId = options.marketId?.trim() ?? "";
  const window = options.window ?? "1d";
  const routeMarketId =
    provider && marketId ? normalizeMarketIdForRoute(provider, marketId) : "";
  const enabled = options.enabled ?? Boolean(provider && routeMarketId);

  return queryOptions({
    queryKey: hip3QueryKeys.marketStats(provider, routeMarketId, window),
    queryFn: ({ signal }) =>
      fetchMarketStats({
        provider,
        marketId: routeMarketId,
        window,
        signal,
      }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    retryDelay: 1_500,
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function buildMarketHistoryQueryOptions(options: UseMarketHistoryOptions) {
  const provider = options.provider?.trim() ?? "";
  const marketId = options.marketId?.trim() ?? "";
  const window = options.window ?? "1d";
  const interval = options.interval ?? "1h";
  const routeMarketId =
    provider && marketId ? normalizeMarketIdForRoute(provider, marketId) : "";
  const enabled = options.enabled ?? Boolean(provider && routeMarketId);

  return queryOptions({
    queryKey: hip3QueryKeys.marketHistory(provider, routeMarketId, window, interval),
    queryFn: ({ signal }) =>
      fetchMarketHistory({
        provider,
        marketId: routeMarketId,
        window,
        interval,
        signal,
      }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    retryDelay: 1_500,
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useTrendingMarkets(options: UseTrendingMarketsOptions = {}) {
  const window = options.window ?? "24h";
  const limit = options.limit ?? 20;

  return useQuery({
    queryKey: hip3QueryKeys.trending(window, limit),
    queryFn: ({ signal }) => fetchTrendingMarkets({ window, limit, signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    retryDelay: 1_500,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useMarketStats(options: UseMarketStatsOptions) {
  return useQuery(buildMarketStatsQueryOptions(options));
}

export function useMarketHistory(options: UseMarketHistoryOptions) {
  return useQuery(buildMarketHistoryQueryOptions(options));
}

export function useEcosystemOverview(options: UseEcosystemOverviewOptions = {}) {
  const window = options.window ?? "7d";

  return useQuery({
    queryKey: hip3QueryKeys.ecosystemOverview(window),
    queryFn: ({ signal }) => fetchEcosystemOverview({ window, signal }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useEcosystemHistory(options: UseEcosystemHistoryOptions) {
  const window = options.window ?? "30d";
  const interval = options.interval ?? "4h";

  return useQuery({
    queryKey: hip3QueryKeys.ecosystemHistory(options.metric, window, interval),
    queryFn: ({ signal }) =>
      fetchEcosystemHistory({
        metric: options.metric,
        window,
        interval,
        signal,
      }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useProviderSummary(options: UseProviderSummaryOptions = {}) {
  const window = options.window ?? "7d";
  const sort = options.sort ?? "oiShare";

  return useQuery({
    queryKey: hip3QueryKeys.providerSummary(window, sort),
    queryFn: ({ signal }) => fetchProviderSummary({ window, sort, signal }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useProviderHistory(options: UseProviderHistoryOptions) {
  const window = options.window ?? "30d";
  const interval = options.interval ?? "4h";

  return useQuery({
    queryKey: hip3QueryKeys.providerHistory(options.metric, window, interval),
    queryFn: ({ signal }) =>
      fetchProviderHistory({
        metric: options.metric,
        window,
        interval,
        signal,
      }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useMarketLeaderboard(options: UseMarketLeaderboardOptions) {
  const window = options.window ?? "24h";
  const limit = options.limit ?? 10;

  return useQuery({
    queryKey: hip3QueryKeys.marketLeaderboard(
      options.metric,
      window,
      limit,
      options.provider,
      options.category
    ),
    queryFn: ({ signal }) =>
      fetchMarketLeaderboard({
        metric: options.metric,
        window,
        limit,
        provider: options.provider,
        category: options.category,
        signal,
      }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useProviderLeaderboard(options: UseProviderLeaderboardOptions) {
  const window = options.window ?? "24h";
  const limit = options.limit ?? 10;

  return useQuery({
    queryKey: hip3QueryKeys.providerLeaderboard(options.metric, window, limit),
    queryFn: ({ signal }) =>
      fetchProviderLeaderboard({
        metric: options.metric,
        window,
        limit,
        signal,
      }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}

export function useHighlights(options: UseHighlightsOptions = {}) {
  const window = options.window ?? "24h";

  return useQuery({
    queryKey: hip3QueryKeys.highlights(window),
    queryFn: ({ signal }) => fetchHighlights({ window, signal }),
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled: options.enabled ?? true,
    retry: 1,
    retryDelay: 1_500,
    placeholderData: keepPreviousData,
  });
}
